import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import styled from 'styled-components/macro'
import log from 'loglevel'
import { DropdownSearchInput } from 'src/components/Reusable/Inputs/DropdownSearchInput'
import { DROPDOWN_SEARCH_THRESHOLD } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { SpaceProps } from 'styled-system'
import {
  UserRecord,
  UserState,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { CreateOrUpdatePolicyRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useUpdateControlFields } from 'src/components/async/model/control/useControlMutations'
import { useCreateOrUpdateRisks } from 'src/pages/Risks/risks.hooks'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { Questionnaire } from '@trustero/trustero-api-web/lib/questionnaire/questionnaire_pb'
import { useUpdateQuestionnaire } from 'src/pages/SecurityQuestionnaire/securityQuestionnaire.hooks'
import { AttestationRecord } from '@trustero/trustero-api-web/lib/vendormanagement/attestation_pb'
import { UpdateVendorRequest } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  ResourceWithAuthz,
  useHasRequiredCapabilities,
} from 'src/app/AppAuth/AppAuth.hooks'
import { useUpdateVendors } from 'src/pages/Vendors/vendors.hooks'
import useClickOutside from '../../../../Utils/useClickOutside'
import * as keyDown from '../../Dropdown/keyboardUtils'
import {
  DropdownItem,
  DropdownItemUser,
  DropdownMenu,
  DropdownSelectedIcon,
} from '../../Dropdown/styles'
import { Tooltip } from '../../Tooltip'
import { IconButton, IconButtonVariant } from '../IconButton'
import { Gravatar } from '../../../Gravatar'
import { useAuthorizedGrpcClientWithContentUpdate } from '../../../../adapter/grpcClient'
import { useUsers } from '../../../async/Users/useUsers'
import { Owner as OwnerEnum } from '../../../../Utils/globalEnums'

const OwnerAssignmentContainer = styled.div`
  position: relative;
`

const OwnerUserList = styled.div`
  max-height: 240px;
  overflow-y: auto;
`

// Each model type saves its owner through a different rpc, so the capability comes
// from the one this button will call. Deriving it here rather than at the call
// sites keeps it from drifting out of step with modelType. A modelType the save
// switch does not handle returns nothing to check, matching the switch's default.
const ownerCapabilities = (modelType?: MODEL_TYPE): CAPABILITY[] => {
  switch (modelType) {
    case MODEL_TYPE.CONTROL:
      return [CAPABILITY.CAN_UPDATE_CONTROLS]
    case MODEL_TYPE.POLICY:
      return [CAPABILITY.CAN_ADD_POLICIES, CAPABILITY.CAN_UPDATE_POLICIES]
    case MODEL_TYPE.RISK:
      return [CAPABILITY.CAN_ADD_RISKS, CAPABILITY.CAN_UPDATE_RISKS]
    case MODEL_TYPE.VENDOR:
      return [CAPABILITY.CAN_UPDATE_VENDORS]
    case MODEL_TYPE.QUESTIONNAIRE:
      return [
        CAPABILITY.CAN_UPDATE_QUESTIONNAIRES,
        CAPABILITY.CAN_UPDATE_ANSWER,
      ]
    default:
      return []
  }
}

type OwnerAssignmentButtonProps = SpaceProps & {
  model?: Risk | Questionnaire | AttestationRecord
  modelId: string
  modelType?: MODEL_TYPE // Used to discern Risks/AuditBot Runs from Controls/Policies
  email: string | null
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  isDisabled?: boolean
  onUpdate?: () => void
  id?: string
  placement?: 'left' | 'right'
}

export const OwnerAssignmentButton = ({
  model,
  modelId,
  modelType,
  email = OwnerEnum.UNASSIGNED,
  requiredCapabilities,
  resource,
  isDisabled,
  onUpdate,
  placement = 'left',
  ...spaceProps
}: OwnerAssignmentButtonProps): JSX.Element => {
  const hasPermission = useHasRequiredCapabilities(
    requiredCapabilities ?? ownerCapabilities(modelType),
    resource,
  )
  const response = useUsers()
  const [menuIsToggled, setMenuIsToggled] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [ownerSearch, setOwnerSearch] = useState('')
  const containerRef = useClickOutside(() => {
    setMenuIsToggled(false)
    setOwnerSearch('')
  }) as RefObject<HTMLDivElement>

  const activeUsers = useMemo(() => {
    const activeUsers: UserRecord.AsObject[] = [
      {
        name: OwnerEnum.UNASSIGNED,
        email: OwnerEnum.UNASSIGNED,
      } as UserRecord.AsObject,
    ]
    if (response.data) {
      activeUsers.push(
        ...response.data
          .getUsersList()
          .map((userMsg) => {
            return userMsg.toObject()
          })
          .filter((user) => user.state === UserState.USER_ACTIVE),
      )
    }
    return activeUsers
  }, [response.data])

  const filteredUsers = useMemo(() => {
    if (!ownerSearch) return activeUsers
    const q = ownerSearch.toLowerCase()
    return activeUsers.filter(
      (u) =>
        u.email?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q),
    )
  }, [activeUsers, ownerSearch])

  // List of nodes
  const nodesRef = useRef<HTMLButtonElement[]>([])
  // If the list of users change, update the length of nodesRef
  useEffect(() => {
    nodesRef.current = nodesRef.current.slice(0, filteredUsers.length)
  }, [filteredUsers])

  const DropdownUser = ({
    user,
    idx,
  }: {
    user: UserRecord.AsObject
    idx: number
  }) => {
    const createOrUpdateRisks = useCreateOrUpdateRisks()
    const updateQuestionnaire = useUpdateQuestionnaire()
    const updateVendors = useUpdateVendors()
    const updateControlFields = useUpdateControlFields()
    const policyClient =
      useAuthorizedGrpcClientWithContentUpdate(PolicyPromiseClient)

    const updateUser = async (e: React.MouseEvent): Promise<void> => {
      e.preventDefault()
      e.stopPropagation()
      setMenuIsToggled(false)
      const email = e.currentTarget.getAttribute('data-email') ?? ''

      // Handle updating various model types
      switch (modelType) {
        case MODEL_TYPE.RISK: {
          if (!model) {
            return
          }
          const risk: Risk = model as Risk
          risk.setOwnerEmail(email)
          try {
            await createOrUpdateRisks([risk])
          } catch (err) {
            log.error(`Error setting owner for Risk ID ${modelId}`, err)
          }
          break
        }
        case MODEL_TYPE.QUESTIONNAIRE: {
          if (!model) {
            return
          }
          const questionnaire = model as Questionnaire
          questionnaire.setOwner(email) as Questionnaire
          try {
            await updateQuestionnaire(questionnaire)
          } catch (err) {
            log.error(
              `Error setting owner for AI GRC Q&A result ID ${modelId}`,
              err,
            )
          }
          break
        }
        case MODEL_TYPE.VENDOR: {
          if (!modelId) {
            return
          }
          const request = new UpdateVendorRequest()
            .setId(modelId)
            .setOwnerEmail(new StringValue().setValue(email))
          try {
            await updateVendors([request])
          } catch (err) {
            log.error(`Error setting owner for Vendor ID ${modelId}`, err)
          }
          break
        }
        case MODEL_TYPE.CONTROL: {
          // Controls - modelId is the control's row id
          try {
            await updateControlFields({
              controlId: modelId,
              ownerEmail: email,
            })
            onUpdate?.()
          } catch (err) {
            log.error(`Error setting owner for control id: ${modelId}`, err)
          }
          break
        }
        case MODEL_TYPE.POLICY: {
          // Policies - modelId is the policy's row id
          const request = new CreateOrUpdatePolicyRequest()
            .setId(modelId)
            .setOwnerEmail(new StringValue().setValue(email))
          try {
            await policyClient.createOrUpdate(request)
            onUpdate?.()
          } catch (err) {
            log.error(`Error setting owner for policy id: ${modelId}`, err)
          }
          break
        }
        default:
          return
      }
    }

    return (
      <DropdownItem
        key={user.email}
        ref={(node) => {
          if (node && node !== nodesRef.current[idx]) {
            nodesRef.current[idx] = node
          }
        }}
        data-email={user.email}
        title={user.name ? `${user.name} (${user.email})` : user.email ?? ''}
        onKeyDown={keyDown.item(idx, nodesRef, setMenuIsToggled)}
        onClick={updateUser}
      >
        <DropdownItemUser>
          <Gravatar email={user.email} />
          <p>{user.name || user.email}</p>
        </DropdownItemUser>
        {user.email === email ? <DropdownSelectedIcon /> : <></>}
      </DropdownItem>
    )
  }
  const tooltipBody = email
    ? email
    : isDisabled || !hasPermission
    ? 'You do not have permission to change the owner'
    : 'Change Owner'

  return (
    <>
      <Tooltip
        id={`owner-assignment-tooltip-${modelId}`}
        show={showTooltip}
        onToggle={(nextShow) => {
          setShowTooltip(menuIsToggled ? false : nextShow)
        }}
        placement="top"
        tooltipBody={tooltipBody}
      >
        <OwnerAssignmentContainer ref={containerRef}>
          <IconButton
            disabled={isDisabled || !hasPermission}
            variant={IconButtonVariant.icon}
            onClick={(e) => {
              e.preventDefault()
              setShowTooltip(false)
              setMenuIsToggled((state) => !state)
            }}
            onKeyDown={keyDown.toggle(nodesRef, setMenuIsToggled)}
            {...spaceProps}
          >
            <Gravatar email={email} />
          </IconButton>
          <DropdownMenu
            aria-expanded={menuIsToggled}
            role="list"
            isToggled={menuIsToggled}
            right={placement === 'left' ? 0 : undefined}
            left={placement === 'right' ? 0 : undefined}
            width="320px"
            style={{
              borderRadius: 8,
              backgroundColor: 'white',
              overflow: 'hidden',
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            {(activeUsers.length > DROPDOWN_SEARCH_THRESHOLD ||
              !!ownerSearch) && (
              <DropdownSearchInput
                value={ownerSearch}
                onChange={setOwnerSearch}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                autoFocus={menuIsToggled}
              />
            )}
            <OwnerUserList>
              {filteredUsers.map((user, idx) => (
                <DropdownUser
                  key={`${modelId}-${user.id}`}
                  user={user}
                  idx={idx}
                />
              ))}
            </OwnerUserList>
          </DropdownMenu>
        </OwnerAssignmentContainer>
      </Tooltip>
    </>
  )
}
